import { h, Component, ComponentChild } from 'preact'
import Table from '../table/Table'
import ButtonControl from '../button-control'
import axios from 'axios'
import AlertComponent from '../ui-components/AlertComponent'
import Loader from '../ui-components/Loader'
import { EditModal } from './EditModal'
import { DeleteModal } from './DeleteModal'

interface Type {
  id: number,
  name: string,
  eventsCount: number
}

interface State {
  types: Array<Type>,
  error?: string,
  typeToDelete?: Type,
  typeToEdit?: Type,
  showNewTypeModal: boolean,
  isLoading: boolean,
}

interface Props {}

interface Row {
  header?: string,
  accessor: string,
  cell: string|ComponentChild,
  colspan?: number
}

class EventTypeOverview extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      types: [],
      isLoading: true,
      showNewTypeModal: false
    }
  }

  async componentDidMount () {
    try {
      const res = await axios.get('/settings/eventTypes', {
        headers: {
          'Accept': 'application/json'
        }
      })
      this.setState({ types: res.data.types, isLoading: false })
    } catch (err) {
      this.setState({ error: 'Failed to fetch event types.', isLoading: false })
      throw err
    }
  }

  async deleteType (typeToDelete: Type) {
    try {
      await axios.delete(`/settings/eventType/${typeToDelete.id}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      this.setState({ types: this.state.types.filter((type: Type) => typeToDelete !== type) })
    } catch (err) {
      this.setState({ error: 'Failed to delete an event.', isLoading: false })
      console.error(err)
    }
  }

  async editType (typeToEdit: Type, newName: string) {
    try {
      const res = await axios.post(`/settings/eventType/${typeToEdit.id}`,
        { oldName: typeToEdit.name, newName: newName },
        { headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        } }
      )
      this.setState({ types: res.data.types, isLoading: false })
    } catch (err) {
      this.setState({ error: 'Failed to update an event.', isLoading: false })
      console.error(err)
    }
  }

  async createType (newType: string) {
    try {
      const res = await axios.put(
        `/settings/eventType`,
        { typeName: newType },
        { headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        } }
      )
      this.setState({ types: res.data.types, isLoading: false })
    } catch (err) {
      this.setState({ error: 'Failed to update an event.', isLoading: false })
      console.error(err)
    }
  }

  render (props: Props, { types, error, isLoading, typeToDelete, typeToEdit, showNewTypeModal }: State) {
    const rows = types.map((type: Type): Array<Row> => [{
      header: 'Event types',
      accessor: 'type-name',
      cell: <span class='pill pill--uppercase qa-type-pill'>{type.name}</span>
    }, {
      header: 'Used by',
      accessor: 'used-by',
      cell: type.eventsCount.toString()
    }, {
      header: 'Actions',
      accessor: 'actions',
      cell: <span>
        <ButtonControl title='Edit' onClick={() => this.setState({ typeToEdit: type })} />
        {/*
          commented out till delete will be implemented in community
         <ButtonControl title={'Delete'} variant={'warning'} onClick={() => this.setState({ typeToDelete: type })} />
        */}
      </span>
    }])
    rows.push([{
      accessor: 'new-type',
      cell: <ButtonControl title='Add new event type' variant='primary' onClick={() => this.setState({ showNewTypeModal: true })} />,
      colspan: 3
    }])

    return <div class='event-types-overview qa-event-types-overview'>
      <div class='control-header'>
        <h2>Event Types</h2>
      </div>
      {isLoading && <Loader />}
      <div class='control-body container'>
        {
          error
            ? <AlertComponent messageType={'error'} message={error} isVisibleFor={5000} />
            : null
        }
        {typeToDelete ? <DeleteModal type={typeToDelete.name}
          onClose={() => this.setState({ typeToDelete: undefined })}
          onDelete={() => this.setState({ typeToDelete: undefined, isLoading: true }, () => this.deleteType(typeToDelete))}
        /> : null}
        {typeToEdit ? <EditModal type={typeToEdit.name}
          onClose={() => this.setState({ typeToEdit: undefined })}
          onSave={(newName: string) => this.setState({ typeToEdit: undefined, isLoading: true }, () => this.editType(typeToEdit, newName))}
        /> : null}
        {showNewTypeModal ? <EditModal onClose={() => this.setState({ showNewTypeModal: false })}
          onSave={(newName: string) => this.setState({ showNewTypeModal: false, isLoading: true }, () => this.createType(newName))} /> : null}
        <Table rows={rows} classes={['event-types__table']} />
      </div>
    </div>
  }
}

export default EventTypeOverview
